/**
 * CanvasResidence — le canvas habite la coque (#856).
 *
 * ## Ce qui change
 *
 * Le canvas était monté par l'écran assistant. Aller consulter un dossier
 * démontait donc l'écran, et avec lui **le document ouvert** : dix minutes de
 * rédaction perdues pour un aller-retour. Un document Word ne se rejoue pas
 * depuis un tampon de blocs — les frappes de l'avocat, le gabarit chargé, les
 * commentaires et les révisions ne vivent que dans l'éditeur.
 *
 * Ce composant est monté **une fois** par `DashboardLayout`, au niveau où
 * vivent déjà la barre latérale et le pont temps réel. Il rend chaque document
 * chaud par un portail keyé dans le nœud hôte stable de son scope documentaire
 * (`state/canvasHostElement.ts`) ; les écrans se contentent d'offrir un
 * emplacement (`CanvasSlot`) où le seul host sélectionné vient se poser. Les
 * autres hosts restent au parking `aria-hidden` + `inert`. Navigation,
 * sélection et masquage ne détruisent aucun résident chaud ; seule une purge
 * de compte les dispose tous explicitement.
 *
 * ## Conséquence à ne pas perdre de vue
 *
 * **Plus rien ne doit dépendre du démontage d'un composant React** —
 * enregistrement, nettoyage, libération. C'est le mécanisme exact de #845, où
 * un export logé dans un nettoyage React s'exécutait après la destruction de
 * l'éditeur et produisait une archive vide, avec un « Enregistré ✓ » à l'écran.
 * Ici la mémoire est la source de vérité et le montage n'est plus qu'un effet
 * d'affichage.
 *
 * Revers de la même médaille : ce qui devait disparaître avec le démontage doit
 * maintenant être **explicitement évincé**. D'où le bail de résidence
 * (`state/canvasEviction.ts`) : au changement de compte, son numéro change et
 * tout ce qui vit dans cette résidence — y compris l'état accumulé du streaming,
 * qui n'est joignable par aucun store — est reconstruit à neuf.
 *
 * ## Les deux montages, et pourquoi ils sont distincts
 *
 * 1. Le suivi du streaming de chaque conversation est monté **avant son
 *    scope physique**. C'est lui qui ouvre le canvas au premier bout de texte ;
 *    attendre SuperDoc créerait une dépendance circulaire et perdrait ce bloc.
 * 2. Le panneau d'une session n'est monté qu'une fois son scope créé après un
 *    vrai geste documentaire. Il survit ensuite à la navigation, visible ou
 *    parqué. SuperDoc pèse ~1,7 Mo compressés : un chat pur ne crée donc toujours
 *    aucun résident.
 */

import * as React from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'sonner';
import { RedactionPanel } from './RedactionPanel';
import {
  isAgentDocumentLoadFailureForConversation,
  masquerLeCanvas,
  useCanvasVisibiliteStore,
} from '../state/canvasVisibilite';
import {
  useRedactionStreamingState,
  type RedactionStreamingState,
} from '../streaming/useRedactionStreamingState';
import {
  deciderArretDuTravailAffiche,
  redactionStreamHub,
} from '../streaming/redactionStreamHub';
import { flushCanvasDraftNow } from '../hooks/useCanvasAutoSave';
import { getCanvasHostElement } from '../state/canvasHostElement';
import { canvasDocumentRuntime } from '../state/canvasDocumentRuntime';
import { CanvasDocumentScopeProvider } from '../state/canvasDocumentScopeContext';
import { useCanvasResidenceLease } from '../state/canvasEviction';
import { useCanvasStageStore } from '../state/canvasStage';
import { useCanvasEditorStore } from '../state/canvasEditorStore';
import {
  installerLaConfirmationDuCheckpointInitial,
  preparerLeBrouillonAvantOuvertureHumaine,
  signalerUnContenuDeBrouillonIllisible,
} from '../state/canvasDocumentOrchestration';
import { ISSUES_QUI_AUTORISENT_LE_MONTAGE } from '../state/canvasHumanOpening';
import { useWebSocketPool } from '@/features/hector-agent-casediscovery-v3/providers/WebSocketPoolProvider';
import { AppErrorBoundary } from '@/components/AppErrorBoundary';
import {
  CanvasDraftContentUnreadableError,
  type CanvasResidentRequest,
} from '../state/canvasResidenceCapacity';

/**
 * **Masquer** le canvas — le geste de l'avocat sur la croix du panneau.
 *
 * Le mot compte (2026-08-13) : rien n'est fermé, rien n'est perdu. Le document
 * garde son porteur, son brouillon et sa session ; Hector peut continuer d'y
 * écrire, et le canvas reparaîtra tel quel au retour dans sa conversation.
 *
 * Il n'y a ni destruction ni checkpoint sur ce geste. L'autosave conserve son
 * propre debounce ; la croix ne doit jamais attendre un export ou un réseau
 * avant de parker le host encore chaud.
 */
function masquerDepuisLeBandeau(conversationId: string): void {
  masquerLeCanvas(conversationId);
}

/** Explains capacity to the lawyer without leaking session ids from diagnostics. */
function messageDEchecResidence(error: unknown, fallbackMessage: string): string {
  if (
    error instanceof Error
    && error.name === 'CanvasResidenceUnavailableError'
    && (error as { kind?: unknown }).kind === 'capacity'
  ) {
    return 'Les trois éditeurs Canvas sont déjà utilisés par des documents actifs. '
      + 'Ferme un Canvas ou attends la fin d’une rédaction avant de réessayer.';
  }
  if (
    error instanceof Error
    && error.name === 'CanvasResidenceUnavailableError'
    && (error as { kind?: unknown }).kind === 'transition'
  ) {
    return 'Un autre document est déjà en cours d’ouverture. Réessaie dans quelques secondes.';
  }
  return fallbackMessage;
}

export function CanvasResidence(): React.JSX.Element {
  // Le bail : son numéro change au changement de compte, et tout ce qui vit
  // dans la résidence est reconstruit — y compris l'état accumulé du streaming,
  // qui n'est joignable par aucun store.
  const lease = useCanvasResidenceLease();
  // Installé ICI et pas dans la collection : l'abonnement vit sur le runtime,
  // qui survit au bail. Le réinstaller à chaque changement de compte
  // multiplierait les auditeurs d'un même signal de montage.
  React.useEffect(() => installerLaConfirmationDuCheckpointInitial(), []);
  return <CanvasResidentCollection key={lease} />;
}

function CanvasResidentCollection(): React.JSX.Element | null {
  const aDejaEteDeploye = useCanvasVisibiliteStore((s) => s.aDejaEteDeploye);
  const canvasDeployed = useCanvasVisibiliteStore((s) => s.deploye);
  const canvasRequestSequence = useCanvasVisibiliteStore((s) => s.requestSequence);
  const residenceRequest = useCanvasVisibiliteStore((s) => s.residenceRequest);
  const holderConversationId = useCanvasEditorStore((s) => s.holderConversationId);
  const stageConversationId = useCanvasStageStore((s) => s.conversationId);
  const [registry, setRegistry] = React.useState(() => canvasDocumentRuntime.getSessionsSnapshot());
  const residenceRequests = React.useRef(new Map<string, {
    controller: AbortController;
    promise: Promise<unknown>;
  }>());
  const failedResidenceRequests = React.useRef(new Set<string>());
  const previousRequestSequence = React.useRef(canvasRequestSequence);

  const requestResidence = React.useCallback((
    request: CanvasResidentRequest,
    fallbackMessage: string,
  ): void => {
    if (
      residenceRequests.current.has(request.conversationId)
      || failedResidenceRequests.current.has(request.conversationId)
    ) return;
    const controller = new AbortController();
    // Sélectionner un résident déjà chaud ne charge rien : le scope SuperDoc
    // exact existe et est prêt. L'admission reste nécessaire pour sérialiser
    // la sélection, mais annoncer une « préparation » dans ce cas transforme
    // chaque navigation entre conversations en faux chargement.
    const targetTemperature = canvasDocumentRuntime.getSessionsSnapshot().sessions.find(
      (session) => session.conversationId === request.conversationId,
    )?.temperature;
    const announcesHydration = targetTemperature !== 'hot';
    const pending = canvasDocumentRuntime.requestResident({ ...request, signal: controller.signal });
    const entry = { controller, promise: pending };
    residenceRequests.current.set(request.conversationId, entry);
    // Une admission froide peut prendre plusieurs secondes (lecture du
    // brouillon, lazy-load SuperDoc, puis montage). L'état doit occuper le
    // panneau dès ce moment ; un toast disparaissait justement hors du regard
    // de l'avocat alors que son Canvas restait blanc.
    if (announcesHydration) {
      useCanvasVisibiliteStore.getState().beginResidenceRequest(
        request.conversationId,
        'canvas-residence',
      );
    }
    void pending.then(
      () => {
        if (announcesHydration) {
          useCanvasVisibiliteStore.getState().settleResidenceRequest(request.conversationId);
        }
      },
      (error: unknown) => {
        failedResidenceRequests.current.add(request.conversationId);
        if (announcesHydration) {
          useCanvasVisibiliteStore.getState().failResidenceRequest(request.conversationId);
        }
        if (error instanceof CanvasDraftContentUnreadableError) {
          // Restauration IMPOSSIBLE, pas indisponibilité de capacité. Le verdict
          // est publié durablement — il doit survivre à l'absence de montage,
          // qui est précisément ce que cet échec produit — plutôt que rendu en
          // toast, qui disparaît avant que l'avocat ait compris ce qu'il perd.
          signalerUnContenuDeBrouillonIllisible({
            conversationId: request.conversationId,
            draftId: error.draftId,
            cause: error,
          });
          return;
        }
        // React StrictMode remonte la collection en développement. Le même
        // Promise capacité peut donc avoir deux observateurs légitimes ; une
        // clé stable évite de montrer deux fois le même échec réseau.
        console.error(
          `[CanvasResidence] ${messageDEchecResidence(error, fallbackMessage)} `
          + `(conversationId=${request.conversationId})`,
          error,
        );
        toast.error(messageDEchecResidence(error, fallbackMessage));
      },
    ).finally(() => {
      if (residenceRequests.current.get(request.conversationId) === entry) {
        residenceRequests.current.delete(request.conversationId);
      }
    });
  }, []);

  React.useEffect(() => canvasDocumentRuntime.subscribeSessions(setRegistry), []);

  React.useEffect(() => {
    // Un échec de checkpoint ne boucle pas au gré des notifications du
    // registre. Seul un nouveau geste « Ouvrir » réarme la demande exacte.
    if (!canvasDeployed) {
      failedResidenceRequests.current.clear();
      for (const { controller } of residenceRequests.current.values()) {
        controller.abort('the lawyer closed the canvas before this document was ready');
      }
    }
    if (previousRequestSequence.current !== canvasRequestSequence) {
      failedResidenceRequests.current.clear();
      previousRequestSequence.current = canvasRequestSequence;
    }
  }, [canvasDeployed, canvasRequestSequence]);

  React.useEffect(() => {
    if (!aDejaEteDeploye || !canvasDeployed) return;
    // Le registre est relu ici plutôt que pris dans l'état React : une
    // notification de sélection et un rendu de cet effet sont deux commits
    // distincts, et agir sur un registre d'un commit de retard rejouerait une
    // sélection déjà faite.
    const liveRegistry = canvasDocumentRuntime.getSessionsSnapshot();
    // ⚠️ **La conversation visée est NOMMÉE ici, elle n'est plus déduite**
    // (#1336). Ce site lisait « le document courant » en traduisant la
    // conversation par `getDocumentSessionIdForConversation`, c'est-à-dire en
    // demandant au runtime un second nom de la clé qu'il tenait déjà. La scène
    // l'emporte sur le porteur : l'avocat qui navigue vers un fil veut voir le
    // document de CE fil, pas celui qu'il détenait avant de naviguer.
    //
    // Aucun repli sur une session sans propriétaire (#1331) : le Canvas ne
    // s'ouvre que dans un fil confirmé, qui réserve son identité au geste. Une
    // résidence sans cible est donc un déploiement sans document — on ne monte
    // rien plutôt que de fabriquer un document que personne ne possède.
    const targetConversationId = stageConversationId ?? holderConversationId;
    const target = targetConversationId
      ? liveRegistry.sessions.find((session) => session.conversationId === targetConversationId) ?? null
      : null;
    if (!target) return;
    const targetScope = canvasDocumentRuntime.getScope(target.conversationId);
    if (!targetScope) {
      // Le handler agent a libéré sa réservation parce que son DOCX a échoué.
      // Le repli générique ne doit surtout pas la remplacer par une page vierge
      // à la prochaine notification du registre ; seule une nouvelle intention
      // explicite (nouveau tool ou geste Canvas) remplace cet état.
      if (isAgentDocumentLoadFailureForConversation(residenceRequest, target.conversationId)) return;
      // `load_document_into_canvas` a déjà réservé sa graine DOCX et attend le
      // slot assistant. Laisser ce repli demander une graine vierge gagnerait la
      // course et ferait précisément monter SuperDoc avant le document demandé.
      if (canvasDocumentRuntime.hasColdMountReservedForVisibleSlot(target.conversationId)) return;
      // A cold logical session may only enter through the capacity transaction:
      // it preloads its exact draft before choosing/cooling any victim. Mounting
      // outside it would create a blank SuperDoc with no seed.
      //
      // ⚠️ La graine ne décrit QUE le tout premier montage d'une session qui
      // n'a encore aucune ressource logique — `preloadTarget` préfère toujours
      // la graine logique du document quand elle existe. Elle est donc vierge :
      // la remplir depuis une projection d'écran, comme avant #1333, faisait
      // entrer l'identifiant de document d'une AUTRE conversation dans le
      // premier montage de celle-ci.
      //
      // (#1592, chemin 1) Chercher le brouillon du fil AVANT de monter la page
      // vierge. Sans cette recherche, le premier enregistrement automatique de
      // la page vierge publie un identifiant neuf, `deleteOthersForConversation`
      // purge l'ancien, et le document que l'avocat avait enregistré disparaît
      // avec son `.docx`. C'est le même défaut que #1586, par une autre porte.
      //
      // La décision vient de `ISSUES_QUI_AUTORISENT_LE_MONTAGE`, partagée avec
      // le geste humain (`canvasHumanOpening`) : une seconde copie dériverait.
      //
      // ⚠️ **Un refus ne monte RIEN, et c'est voulu.** L'emplacement reste
      // déployé sans document : `RedactionPanel` y rend la carte qui explique
      // l'échec, à partir du verdict que la recherche vient de publier. Monter
      // une page vierge « pour ne pas laisser l'écran vide » détruirait
      // précisément le brouillon qu'on n'a pas su lire.
      //
      // La recherche n'est pas bornée ici : la borne vit dans l'orchestration
      // (#1596), partagée avec le geste humain et l'amorce agent.
      const convId = target.conversationId;
      void (async () => {
        const verdict = await preparerLeBrouillonAvantOuvertureHumaine(convId);
        if (!ISSUES_QUI_AUTORISENT_LE_MONTAGE.has(verdict.issue)) return;
        // RELU APRÈS L'ATTENTE — même patron que le reste du module. La recherche
        // est le premier point de suspension de ce montage : entre son départ et
        // sa réponse, l'avocat a pu refermer le canvas, et un autre chemin a pu
        // monter ce document. Monter quand même poserait un résident que l'écran
        // ne demande plus, et lui ferait disputer l'unique emplacement au document
        // réellement affiché.
        if (!useCanvasVisibiliteStore.getState().deploye) return;
        if (canvasDocumentRuntime.getScope(convId) !== null) return;
        requestResidence({
          conversationId: convId,
          seed: { documentId: null, presentation: null },
          visibility: 'select',
        }, "Ce document n'a pas pu être rouvert. Le document actuel reste disponible ; réessaie.");
      })();
      return;
    }
    if (liveRegistry.visibleConversationId === target.conversationId) {
      // Le porteur affiché est la seule chose que la sélection publie encore :
      // la présentation, elle, n'a jamais quitté son scope.
      useCanvasEditorStore.getState().setHolderConversationId(target.conversationId);
      return;
    }
    if (target.temperature === 'hot' || target.temperature === 'hydrating') {
      requestResidence({
        conversationId: target.conversationId,
        visibility: 'select',
      }, "Ce document n'a pas pu être ouvert. Le document actuel reste disponible ; réessaie.");
    }
  }, [
    aDejaEteDeploye,
    canvasDeployed,
    canvasRequestSequence,
    holderConversationId,
    residenceRequest,
    requestResidence,
    registry.sessions,
    stageConversationId,
  ]);

  return (
    <>
      {/*
        ⚠️ **La clé React d'un résident est sa CONVERSATION NUE, jamais
        `cleDeMontage`** (#1336). L'époque avance à chaque remontage runtime —
        remplacer le document ouvert en est un. La composer dans la clé
        démonterait le panneau à chaque remplacement, c'est-à-dire exactement le
        mécanisme de #1325 : éditeur démonté, canvas vide, travail de l'avocat
        effacé de l'écran. Le remontage de l'éditeur, lui, est décidé plus bas
        par `RedactionPanel.redactionCanvasKey`, composée sur l'ÉPOQUE depuis
        #1337 : un nouveau montage reconstruit l'éditeur, jamais le panneau.
      */}
      {registry.sessions.map((session) => (
        <CanvasResidentLifecycle
          key={session.conversationId}
          holderConversationId={session.conversationId}
          isVisibleResident={
            aDejaEteDeploye
            && canvasDeployed
            && session.conversationId === registry.visibleConversationId
          }
        />
      ))}
    </>
  );
}

/**
 * Observe le flux dès la réservation logique, avant que SuperDoc existe.
 *
 * Le premier bloc est précisément ce qui déploie le canvas agent. Attendre le
 * scope physique pour monter `useRedactionStreamingState` créerait donc une
 * dépendance circulaire : pas de scope avant déploiement, pas de déploiement
 * avant observation du bloc. Le résident physique reste conditionnel, mais
 * son activateur suit chaque identité logique enregistrée.
 */
function CanvasResidentLifecycle({
  holderConversationId,
  isVisibleResident,
}: {
  holderConversationId: string;
  isVisibleResident: boolean;
}): React.JSX.Element | null {
  const streamingState = useRedactionStreamingState(holderConversationId);
  if (!canvasDocumentRuntime.getScope(holderConversationId)) return null;
  return (
    <AppErrorBoundary
      level="zone"
      errorContext={{ action: 'redaction_canvas_resident' }}
    >
      <CanvasResident
        holderConversationId={holderConversationId}
        isVisibleResident={isVisibleResident}
        streamingState={streamingState}
      />
    </AppErrorBoundary>
  );
}

function CanvasResident({
  holderConversationId,
  isVisibleResident,
  streamingState,
}: {
  holderConversationId: string;
  isVisibleResident: boolean;
  streamingState: RedactionStreamingState;
}): React.ReactPortal {
  const caseId = useCanvasStageStore((s) => s.caseId);
  const stageConversationId = useCanvasStageStore((s) => s.conversationId);
  const selectedCaseRef = React.useRef<string | null>(null);
  if (stageConversationId === holderConversationId) selectedCaseRef.current = caseId;
  const pool = useWebSocketPool();

  /**
   * Arrêter la rédaction en cours, depuis le canvas (#855).
   *
   * C'est **exactement** le geste « Arrêter la génération » du chat — même
   * `cancelStream`, donc même trame `user_interrupt`, même libération du pin exact
   * (`WebSocketPool.cancelStream`). Rien de spécifique au canvas ici : dupliquer
   * l'arrêt donnerait deux façons d'interrompre le même tour, qui divergeraient
   * au premier changement de protocole.
   *
   * Le vidage suit l'arrêt, il ne le précède pas : « annuler » veut dire *arrête
   * d'écrire*, pas *efface ce que tu as écrit* — on enregistre donc le document
   * tel qu'il est au moment où l'agent lâche la plume. Sans lui, le texte
   * n'atteindrait le brouillon qu'au terme du débounce de 2,5 s de
   * l'enregistrement automatique, qu'un rechargement immédiat devancerait.
   *
   * La résidence n'essaie pas de retrouver elle-même une clé WebSocket : elle
   * ne connaît que la conversation qui possède le document, et celle-ci survit
   * à la navigation. Le pool résout son transport vivant puis refuse l'arrêt si
   * job ou conversation ne correspondent plus.
   */
  const arreterLaRedaction = React.useMemo(
    () => () => {
          // Un callback React peut survivre le temps d'un clic au rendu qui l'a
          // créé. Si un job plus récent a déjà pris ce document, l'ancien
          // bouton ne doit interrompre ni son tour WS ni son flux SSE.
          //
          // ⚠️ Le hub est interrogé sur la conversation du RÉSIDENT, jamais sur
          // celle qu'affiche la scène (#1336) : un panneau parqué garde son
          // bouton d'arrêt, et l'avocat qui l'actionne veut arrêter CE
          // document-là. L'ancien code passait l'identité documentaire que
          // l'état de streaming republiait ; cette identité n'existe plus, et
          // le porteur que ce résident tient déjà la remplace exactement.
          const decisionDArret = deciderArretDuTravailAffiche({
            jobIdAffiche: streamingState.jobId,
            dernierJobIdDeLaSession:
              redactionStreamHub.derniereEcoutePourConversation(holderConversationId)?.jobId
              ?? null,
          });
          if (decisionDArret.statut === 'ignore') return;
          // ⚠️ **Aucune époque de montage ne voyage avec ce geste** (#1336,
          // arbitrage du 2026-09-10). Ce site est le SEUL appelant de
          // `cancelCanvasWork`, et il a été le lieu du seul désaccord de
          // conception du chantier. Ce qui périme un « Arrêter » retenu par un
          // callback React, c'est que son TRAVAIL ait été remplacé — le filtre
          // de job ci-dessus l'attrape, et le pool le revérifie sur le tour
          // qu'il a capturé. Le montage, lui, avance pour des raisons qui ne
          // périment rien (refroidissement LRU, remplacement de gabarit,
          // remontage React) : le lui opposer aurait fait échouer en silence
          // l'arrêt d'un travail toujours vivant. Voir le TSDoc de
          // `CanvasWorkIdentity` (`WebSocketPool.ts`) pour la démonstration.
          if (!pool.cancelCanvasWork({
            conversationId: holderConversationId,
            jobId: decisionDArret.jobId,
          })) {
            // Rien n'est parti : le pin reste pris, le bandeau reste posé. Le
            // dire est la seule chose honnête — un bandeau immobile sans un mot
            // se lit comme une panne, et prétendre l'arrêt réussi laisserait
            // l'avocat rouvrir un document qu'Hector peut encore réécrire.
            // Le verrou n'est pas éternel pour autant : une reconnexion épuisée
            // libère le pin par la soupape de reconnexion.
            toast.error(
              'La rédaction n\'a pas pu être arrêtée. La connexion à Hector est '
              + 'momentanément perdue. Réessaie dans quelques secondes.',
            );
            return;
          }
          // Le flux d'un acte généré ne marque **pas** le canvas modifié à
          // chaque bloc (un enregistrement par jeton serait ruineux) : il ne le
          // fait qu'à la fin du flux, qui n'arrivera plus. Sans ce marquage, le
          // vidage ci-dessous n'aurait rien à envoyer et le texte produit
          // n'atteindrait le brouillon qu'au prochain geste de l'avocat — ou
          // jamais. C'est exactement la promesse « annuler conserve ».
          // Arrêter le tour ne suffit pas depuis que l'écoute vit hors de React
          // (#857) : le flux de l'acte continuerait d'écrire dans le document
          // sous les yeux de l'avocat qui vient de dire stop. C'est l'annulation
          // explicite — la seule chose, avec la fin du travail, qui arrête une
          // écoute.
          if (decisionDArret.jobId) {
            redactionStreamHub.annuler(decisionDArret.jobId, 'arrêt demandé par l\'avocat');
          }
          canvasDocumentRuntime.getDraftController(holderConversationId)?.markDirty();
          // Le contrôleur est armé synchroniquement ci-dessus. Le tour de boucle
          // laisse SuperDoc terminer sa transaction d'annulation avant l'export,
          // tout en gardant la destination figée sur ce document exact.
          setTimeout(() => flushCanvasDraftNow(holderConversationId), 0);
        },
    [pool, holderConversationId, streamingState.jobId],
  );

  const scope = canvasDocumentRuntime.getScope(holderConversationId);
  if (!scope) {
    throw new Error(
      `Canvas residence lost conversationId=${holderConversationId} before portal creation. `
      + 'Account purge must remount the residence lease before rendering another canvas.',
    );
  }

  return createPortal(
    <CanvasDocumentScopeProvider scope={scope}>
      <RedactionPanel
        state={streamingState}
        selectedCaseId={selectedCaseRef.current}
        conversationId={holderConversationId}
        isVisibleResident={isVisibleResident}
        onClose={() => masquerDepuisLeBandeau(holderConversationId)}
        onArreterRedaction={arreterLaRedaction}
      />
    </CanvasDocumentScopeProvider>,
    getCanvasHostElement(holderConversationId),
    holderConversationId,
  );
}
